import {
  ChangeDetectionStrategy,
  Component,
  Input,
  signal,
  computed,
  OnInit,
  ChangeDetectorRef,
  inject,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService, MenuItem } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { MenuModule } from 'primeng/menu';
import { TranslateService } from '@ngx-translate/core';

import {
  AddFormComponent,
  TableSection,
} from '@shared/components/add-form/add-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { PermissionService } from '@app/core/services/permission.service';
import {
  OnlineStoreFormConfig,
  convertSalesOrganizationToOptions,
  convertPhysicalStoreToOptions,
  CHANNEL_SUB_TYPE_OPTIONS,
  VIRTUAL_WAREHOUSE_TYPE,
} from '../../online-store.config';
import {
  isLazadaChannelCode,
  isLazadaOnlineStore,
  isLazadaOnlineStoreFormContext,
} from '../../../utils/lazada-integration.util';
import { isShopeeOnlineStore, isShopeeChannelCode } from '../../../utils/shopee-integration.util';
import {
  type ProductFieldMappingRow,
  type SkuProductFieldOption,
  serializeFieldMappingToConfig,
  buildProductFieldMappingTableSection,
  PRODUCT_FIELD_MAPPING_SECTION_NAME,
} from '@shared/components/product-field-mapping';
import { GoodsSkuControllerService } from '@app/api/goods-service/api/goodsSkuController.service';
import { OnlineShopControllerService } from '@app/api/channel-service/api/onlineShopController.service';
import { ChannelIntegrationService } from '@app/api/channel-service/api/channelIntegration.service';
import { SalesOrganizationControllerService } from '@app/api/channel-service/api/salesOrganizationController.service';
import { PhysicalShopControllerService } from '@app/api/channel-service/api/physicalShopController.service';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { ListOfTreeVO } from '@app/api/channel-service/model/listOfTreeVO';
import { PhysicalShopShowDTO } from '@app/api/channel-service/model/physicalShopShowDTO';
import { PageRequestDTO } from '@app/api/channel-service/model/pageRequestDTO';
import { ChannelService } from '../../../service/channel.service';
import {
  WarehouseControllerService,
  WarehouseQueryReqDTO,
} from '@app/api/stock-service';
import { Observable, forkJoin, of, from } from 'rxjs';
import { switchMap, catchError, map, finalize } from 'rxjs/operators';
import type { MagentoFieldMappingDTO } from '@app/api/channel-service/model/magentoFieldMappingDTO';
import type { MagentoFieldMappingVO } from '@app/api/channel-service/model/magentoFieldMappingVO';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
  HttpRequest,
} from '@angular/common/http';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { OnlineStoreWarehouseOptionsService } from '../../services/online-store-warehouse-options.service';
import { PaginatedDropdownLoadParams } from '@shared/utils/paginated-dropdown.util';
/**
 * Wrapper for online store add/edit/view form: main form, warehouse menu, product list (Lazada),
 * and product field mapping (Magento). Handles dropdowns, validation, save, and product CRUD.
 */
@Component({
  selector: 'app-online-store-form-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    AddFormComponent,
    MenuModule,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './online-store-form-wrapper.component.html',
  styleUrls: ['./online-store-form-wrapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnlineStoreFormWrapperComponent implements OnInit {
  @Input() config!: OnlineStoreFormConfig;
  @Input() editMode = false;
  @Input() viewMode = false;
  @Input() canUpdate = false;

  formData = signal<any>({});
  /** Original API payload; used when merging on update. */
  originalApiData = signal<any>(null);

  loading = signal(false);
  initialLoading = signal(true);

  companyOptions = signal<Array<{ label: string; value: number }>>([]);
  physicalStoreOptions = signal<Array<{ label: string; value: number }>>([]);

  selectedChannelType = signal<string>('');

  @ViewChild('warehouseMenu') warehouseMenu: any;
  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;
  showWarehouseMenu = signal(false);

  private readonly FORM_DATA_STORAGE_KEY = 'onlineStoreFormDraft';
  private skipCanDeactivate = false;

  tableSections = signal<TableSection[]>([]);

  productListData = signal<any[]>([]);
  productListLoading = signal(false);
  productListTotalRecords = signal(0);
  productListCurrentPage = signal(0);
  productListRows = signal(20);

  showProductDeleteDialog = signal(false);
  productDeleteDialogTitle = signal('');
  productDeleteDialogMessage = signal('');
  productDeleteDialogButtons = signal<any[]>([]);
  productDeleteDialogAction = signal('');
  productDeleteDialogData = signal<{
    productId: string;
    onlineStoreId: string;
  } | null>(null);
  productListSearchParams = signal<any>({});

  /** SKU field options from API /v1/goods/sku/mapping-fields (goods-service). Replaces DEFAULT_SKU_PRODUCT_FIELDS. */
  skuProductFieldOptions = signal<SkuProductFieldOption[]>([]);
  skuProductFieldOptionsLoading = signal(false);

  /** Product field mapping: required rows first, optional rows from Add dropdown. Shown only for Magento. */
  productFieldMappings = signal<ProductFieldMappingRow[]>([]);

  /** Order field mapping removed from UI; kept empty for save payload (orderFieldMappingConfig). */
  orderFieldMappings = signal<ProductFieldMappingRow[]>([]);

  /** Options for Thuộc tính Magento dropdown (from GET v1/online/shop/{shopId}/magento/product/attributes). */
  magentoAttributeOptions = signal<{ label: string; value: string }[]>([]);

  computedSectionButtons = computed(() => {
    let buttons = this.config?.sectionButtons || [];
    if (this.viewMode || this.editMode) {
      buttons = buttons.filter(
        (b: { action?: string }) => b.action !== 'create_warehouse',
      );
    }
    return buttons;
  });

  warehouseMenuItems = computed<MenuItem[]>(() => [
    {
      label: this.translateService.instant(
        'onlineStore.form.placeholders.logical_warehouse',
      ),
      icon: 'material-symbols-outlined',
      command: () => this.navigateToWarehouse('/warehouse/logical/add'),
    },
    {
      label: this.translateService.instant(
        'onlineStore.form.placeholders.virtual_warehouse',
      ),
      icon: 'material-symbols-outlined',
      command: () => this.navigateToWarehouse('/warehouse/virtual/add'),
    },
  ]);

  /**
   * Navigate to warehouse creation page
   */
  private navigateToWarehouse(route: string): void {
    // Save current form data to localStorage before redirecting
    this.saveFormDataToStorage();

    // Navigate to warehouse creation page
    this.router.navigate([route], {
      queryParams: {
        returnUrl: this.router.url, // Store current URL to return to
      },
    });
  }

  /**
   * Save current form data to localStorage
   */
  private saveFormDataToStorage(): void {
    try {
      // Try to get form data from AddFormComponent first (more accurate)
      const form = (this.addFormComponent as any)?.form;
      if (form) {
        const formValue = form.getRawValue();
        if (formValue && Object.keys(formValue).length > 0) {
          localStorage.setItem(
            this.FORM_DATA_STORAGE_KEY,
            JSON.stringify(formValue),
          );
          // Also update formData signal
          this.formData.set(formValue);
        }
      } else {
        // Fallback to formData signal
        const formData = this.formData();
        if (formData && Object.keys(formData).length > 0) {
          localStorage.setItem(
            this.FORM_DATA_STORAGE_KEY,
            JSON.stringify(formData),
          );
        }
      }
    } catch (error) {
      console.error('Error saving form data to storage:', error);
    }
  }

  /**
   * Restore form data from localStorage
   */
  private restoreFormDataFromStorage(): void {
    try {
      const savedData = localStorage.getItem(this.FORM_DATA_STORAGE_KEY);
      if (savedData) {
        const formData = JSON.parse(savedData);

        // Wait for form to be initialized
        const checkForm = () => {
          const form = (this.addFormComponent as any)?.form;
          if (form && formData) {
            // Restore form values
            Object.keys(formData).forEach((key) => {
              const control = form.get(key);
              if (control) {
                control.setValue(formData[key], { emitEvent: false });
              }
            });

            // Update formData signal to keep it in sync
            this.formData.set(formData);

            // Clear storage after restoring
            localStorage.removeItem(this.FORM_DATA_STORAGE_KEY);

            // Trigger change detection
            this.cdr.markForCheck();
          } else {
            // Retry after a short delay if form is not ready
            setTimeout(checkForm, 100);
          }
        };

        checkForm();
      }
    } catch (error) {
      console.error('Error restoring form data from storage:', error);
      // Clear corrupted data
      localStorage.removeItem(this.FORM_DATA_STORAGE_KEY);
    }
  }

  // Computed property to combine all form data for initialData
  combinedInitialData = computed(() => {
    const baseData = {
      ...this.formData(),
    };

    return baseData;
  });

  // Computed property to track current channel type from form data
  currentChannelType = computed(() => {
    return this.formData()?.channelTypeCode || this.selectedChannelType() || '';
  });

  // Memoize platform options to prevent unnecessary updates
  private readonly platformOptionsCache = new Map<
    string,
    Array<{ label: string; value: string }>
  >();

  // Computed property to get form fields with translated default values and dropdown options
  translatedFormFields = computed(() => {
    if (!this.config?.formFields) return [];

    // Read current channel type to make this computed reactive
    const channelType = this.currentChannelType();
    // Read current platform (channelSubTypeCode) to handle Lazada fields visibility
    const currentPlatform = this.formData()?.channelSubTypeCode || '';

    return this.config.formFields.map((field) => {
      let updatedField = { ...field };

      updatedField = this.applyDefaultValueTranslation(field, updatedField);
      updatedField = this.applyModeRestrictions(field, updatedField);
      updatedField = this.applyLazadaAndWebsiteFields(
        field,
        updatedField,
        channelType,
        currentPlatform,
      );

      // Handle Magento Connection section fields - only when websitePlatform = MAGENTO
      const currentWebsitePlatform = this.formData()?.websitePlatform || '';
      const currentMagentoAuthType = this.formData()?.magentoAuthType || '';
      updatedField = this.applyMagentoConnectionFields(
        field,
        updatedField,
        currentWebsitePlatform,
        currentMagentoAuthType,
      );

      // Handle dropdown options: channels that publish products via a virtual
      // warehouse (Magento, Shopee, Lazada) must pick a VIRTUAL warehouse — that
      // selection becomes the channel_store_virtual_warehouse mapping the backend
      // reads to list publishable SKUs (GET /channel-integration/stock has no
      // logical-warehouse fallback). Logical-warehouse-only channels never get that
      // mapping, so their publish UI shows no SKUs.
      if (
        field.controlName === 'channelWarehouseName' &&
        field.type === 'searchableDropdown'
      ) {
        updatedField = {
          ...updatedField,
          paginatedOptions: {
            // Read virtualOnly fresh on each load — the dropdown may first load before the platform
            // (channelSubTypeCode) is set, so a captured value would stay false and never show
            // virtual warehouses. A platform change triggers reloadPaginatedField (onFormValueChanges).
            loadPage: (params: PaginatedDropdownLoadParams) =>
              this.onlineStoreWarehouseOptionsService.getChannelWarehouseOptionsPage(
                params,
                this.isVirtualWarehouseChannel(),
              ),
          },
        };
      } else if (
        field.controlName === 'allocationValue' &&
        field.type === 'number'
      ) {
        updatedField = this.applyAllocationValueField(updatedField);
      } else if (
        field.controlName === 'channelSubTypeCode' &&
        field.type === 'dropdown'
      ) {
        updatedField = this.applyChannelSubTypeField(updatedField, channelType);
      }

      // Hide channel connection section when platform is not selected
      if (
        field.section === 'onlineStore.form.sections.channel_connection_info' &&
        !currentPlatform
      ) {
        updatedField = { ...updatedField, hidden: true };
      }

      return updatedField;
    });
  });

  /** Section buttons: hide Create Warehouse in view and edit mode. */
  effectiveSectionButtons = computed(() => {
    const buttons = this.config?.sectionButtons ?? [];
    if (this.editMode || this.viewMode) {
      return buttons.filter(
        (b: { action?: string }) => b.action !== 'create_warehouse',
      );
    }
    return buttons;
  });

  private applyDefaultValueTranslation(field: any, updatedField: any): any {
    if (
      field.defaultValue &&
      typeof field.defaultValue === 'string' &&
      field.defaultValue.includes('.')
    ) {
      const translatedValue = this.translateService.instant(field.defaultValue);
      if (translatedValue !== field.defaultValue) {
        return { ...updatedField, defaultValue: translatedValue };
      }
    }
    return updatedField;
  }

  private applyModeRestrictions(field: any, updatedField: any): any {
    if (
      this.editMode &&
      (field.controlName === 'code' ||
        field.controlName === 'channelWarehouseName')
    ) {
      updatedField = { ...updatedField, readonly: true, disabled: true };
    }
    if (
      this.viewMode &&
      (field.controlName === 'lazadaAppKey' ||
        field.controlName === 'lazadaAppSecret' ||
        field.controlName === 'shopeePartnerId' ||
        field.controlName === 'shopeePartnerKey')
    ) {
      updatedField = { ...updatedField, disabled: true, readonly: true };
    }
    return updatedField;
  }

  private applyLazadaAndWebsiteFields(
    field: any,
    updatedField: any,
    channelType: string,
    currentPlatform: string,
  ): any {
    if (
      field.controlName === 'lazadaAppKey' ||
      field.controlName === 'lazadaAppSecret'
    ) {
      const visible = isLazadaChannelCode(currentPlatform);
      return {
        ...updatedField,
        hidden: !visible,
        ...(visible
          ? {}
          : { required: false, validators: [], errorMessages: {} }),
      };
    }
    if (
      field.controlName === 'shopeePartnerId' ||
      field.controlName === 'shopeePartnerKey'
    ) {
      const visible = isShopeeChannelCode(currentPlatform);
      return {
        ...updatedField,
        hidden: !visible,
        ...(visible
          ? {}
          : { required: false, validators: [], errorMessages: {} }),
      };
    }
    if (field.controlName === 'websitePlatform') {
      const visible =
        channelType === 'MARKETPLACE' && currentPlatform === 'WEBSITE';
      return {
        ...updatedField,
        hidden: !visible,
        ...(visible
          ? {}
          : { required: false, validators: [], errorMessages: {} }),
      };
    }
    return updatedField;
  }

  private applyMagentoConnectionFields(
    field: any,
    updatedField: any,
    websitePlatform: string,
    authType: string,
  ): any {
    if (field.controlName === 'magentoAuthType') {
      return { ...updatedField, hidden: websitePlatform !== 'MAGENTO' };
    }
    const oauth1aFields = [
      'magentoConsumerKey',
      'magentoConsumerSecret',
      'magentoAccessToken',
      'magentoAccessTokenSecret',
    ];
    if (oauth1aFields.includes(field.controlName)) {
      const visible = websitePlatform === 'MAGENTO' && authType === 'OAUTH_1A';
      return {
        ...updatedField,
        hidden: !visible,
        ...(visible
          ? {}
          : { required: false, validators: [], errorMessages: {} }),
      };
    }
    if (
      field.controlName === 'magentoUsername' ||
      field.controlName === 'magentoPassword'
    ) {
      const visible =
        websitePlatform === 'MAGENTO' && authType === 'INTEGRATION_TOKEN';
      return {
        ...updatedField,
        hidden: !visible,
        ...(visible
          ? {}
          : { required: false, validators: [], errorMessages: {} }),
      };
    }
    if (field.controlName === 'webhookSecret') {
      return {
        ...updatedField,
        hidden: !(this.viewMode && websitePlatform === 'MAGENTO'),
      };
    }
    return updatedField;
  }

  /** Channels that publish via a virtual warehouse (Magento/Shopee/Lazada) → warehouse dropdown lists kho ảo. */
  private isVirtualWarehouseChannel(): boolean {
    const platform = this.formData()?.channelSubTypeCode || '';
    const websitePlatform = this.formData()?.websitePlatform || '';
    return (
      websitePlatform === 'MAGENTO' ||
      isShopeeChannelCode(platform) ||
      isLazadaChannelCode(platform)
    );
  }

  private applyAllocationValueField(updatedField: any): any {
    const warehouseName =
      this.formData()?.channelWarehouseName ||
      this.originalApiData()?.virtualWarehouses?.[0]?.virtualWarehouseName ||
      this.originalApiData()?.channelWarehouseName ||
      '';
    const selectedVirtual =
      warehouseName &&
      this.onlineStoreWarehouseOptionsService
        .getVirtualWarehouseByName()
        .has(warehouseName)
        ? this.onlineStoreWarehouseOptionsService
            .getVirtualWarehouseByName()
            .get(warehouseName)
        : this.originalApiData()?.virtualWarehouses?.[0];
    if (selectedVirtual) {
      const typeCode =
        selectedVirtual.typeCode ?? VIRTUAL_WAREHOUSE_TYPE.DEFAULT;
      const result = { ...updatedField, hidden: false };
      if (
        typeCode === VIRTUAL_WAREHOUSE_TYPE.DEFAULT &&
        !this.formData()?.allocationValue
      ) {
        result.defaultValue = 100;
      }
      return result;
    }
    return {
      ...updatedField,
      hidden: true,
      required: false,
      validators: [],
      errorMessages: {},
    };
  }

  private applyChannelSubTypeField(
    updatedField: any,
    channelType: string,
  ): any {
    let options: Array<{ label: string; value: string }> = [];
    if (channelType) {
      if (this.platformOptionsCache.has(channelType)) {
        options = this.platformOptionsCache.get(channelType)!;
      } else {
        const channelOptions =
          CHANNEL_SUB_TYPE_OPTIONS[
            channelType as keyof typeof CHANNEL_SUB_TYPE_OPTIONS
          ];
        if (channelOptions) {
          options = channelOptions;
          this.platformOptionsCache.set(channelType, options);
        }
      }
    }
    const result = { ...updatedField, options };
    if (this.viewMode && result.dependsOn) {
      const { dependsOn: _d, ...rest } = result;
      return rest;
    }
    return result;
  }

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly warehouseControllerService = inject(
    WarehouseControllerService,
  );
  private readonly permissionService = inject(PermissionService);
  private readonly httpClient = inject(HttpClient);
  private readonly goodsSkuController = inject(GoodsSkuControllerService);
  private readonly channelIntegrationService = inject(ChannelIntegrationService);

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly onlineShopService: OnlineShopControllerService,
    private readonly salesOrganizationService: SalesOrganizationControllerService,
    private readonly physicalShopService: PhysicalShopControllerService,
    private readonly onlineStoreWarehouseOptionsService: OnlineStoreWarehouseOptionsService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly channelService: ChannelService,
  ) {}

  ngOnInit(): void {
    // Load dropdown data first
    this.loadDropdownData();

    // Load existing data if in edit mode
    if (this.editMode || this.viewMode) {
      this.loadExistingData();
    } else {
      // For add mode, restore form data if returning from warehouse creation
      setTimeout(() => {
        this.restoreFormDataFromStorage();
        this.initialLoading.set(false);
      }, 500);
    }
  }

  private loadDropdownData(): void {
    // Load sales organization data
    this.salesOrganizationService.listOfTree(false).subscribe({
      next: async (response: any) => {
        try {
          let data: ListOfTreeVO[];

          // Handle blob response
          if (response instanceof Blob) {
            const jsonData = await BlobConverterUtil.convertToJson(response);
            data = jsonData || [];
          } else {
            data = response || [];
          }

          this.companyOptions.set(convertSalesOrganizationToOptions(data));
        } catch (error) {
          console.error('Error processing sales organization response:', error);
          this.companyOptions.set([]);
        }
      },
      error: (error: any) => {
        console.error('Error loading sales organization:', error);
        this.companyOptions.set([]);
      },
    });

    const pageRequest: PageRequestDTO = {
      page: 0,
      size: 100,
      sortBy: 'creationDate',
      ascending: false,
    };

    // Load physical store data for related store dropdown
    const physicalShopDTO: PhysicalShopShowDTO = {
      name: '',
      typeCode: '',
      manager: '',
      status: true,
      selfPickupFlag: false, // Only get active stores
      deleteFlag: false,
      pageRequest: pageRequest,
    };

    this.physicalShopService
      .showPage(physicalShopDTO, 'body', false)
      .subscribe({
        next: async (response: any) => {
          try {
            let data: any[];

            // Handle blob response
            if (response instanceof Blob) {
              const jsonData = await BlobConverterUtil.convertToJson(response);
              data = jsonData?.pageContent || [];
            } else {
              data = response?.pageContent || [];
            }
            this.physicalStoreOptions.set(convertPhysicalStoreToOptions(data));
          } catch (error) {
            console.error('Error processing physical store response:', error);
            this.physicalStoreOptions.set([]);
          }
        },
        error: (error: any) => {
          console.error('Error loading physical stores:', error);
          this.physicalStoreOptions.set([]);
        },
      });

  }

  private loadExistingData(): void {
    const storeId = this.route.snapshot.params['id'];

    if (storeId) {
      const storeIdNum = Number(storeId);

      this.onlineShopService.showDetails1(storeIdNum).subscribe({
        next: async (response: any) => {
          try {
            // Handle blob response
            if (response instanceof Blob) {
              const jsonData = await BlobConverterUtil.convertToJson(response);
              this.loadFormDataFromApi(jsonData);
            } else {
              // Direct JSON response
              this.loadFormDataFromApi(response);
            }

            // Small delay to ensure form component is ready
            setTimeout(() => {
              this.initialLoading.set(false);
              // Create product list table section in view mode for Lazada or Shopee
              // (the section builder already handles both channels).
              if (
                this.viewMode &&
                (this.isLazadaChannel() || this.isShopeeChannel())
              ) {
                this.createProductListTableSection(storeId);
              }
            }, 100);
          } catch (error) {
            console.error('Error processing online store response:', error);
            this.initialLoading.set(false);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'onlineStore.messages.load_detail_error',
              ),
            });
          }
        },
        error: (error: any) => {
          console.error('Error loading online store:', error);
          this.initialLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'onlineStore.messages.load_detail_error',
            ),
          });
        },
      });
    } else {
      console.error('No store ID found in route parameters');
    }
  }

  /** Load Magento product attributes from GET v1/online/shop/{shopId}/magento/product/attributes (channel-service). */
  loadMagentoAttributeOptions(
    shopId: number,
  ): Observable<{ label: string; value: string }[]> {
    return this.onlineShopService.getMagentoProductAttributes(shopId).pipe(
      switchMap((response: unknown) => {
        if (response instanceof Blob) {
          return from(
            response
              .text()
              .then((text) => {
                const parsed = JSON.parse(text) as unknown;
                let list: unknown[] = [];
                if (Array.isArray(parsed)) list = parsed;
                else if (parsed && typeof parsed === 'object') {
                  const o = parsed as Record<string, unknown>;
                  const arr = o['data'] ?? o['content'] ?? o['pageContent'];
                  if (Array.isArray(arr)) list = arr;
                }
                return list as any[];
              })
              .catch((e: unknown) => {
                throw e instanceof Error ? e : new Error(String(e));
              }),
          );
        }
        let list: unknown[] = [];
        if (Array.isArray(response)) list = response;
        else if (response && typeof response === 'object') {
          const arr =
            (response as Record<string, unknown>)['data'] ??
            (response as Record<string, unknown>)['content'] ??
            (response as Record<string, unknown>)['pageContent'];
          if (Array.isArray(arr)) list = arr;
        }
        return of(list as any[]);
      }),
      map((list: any[]) => {
        const options = (Array.isArray(list) ? list : [])
          .map((d) => ({
            label:
              String(d?.label ?? d?.code ?? '').trim() || String(d?.code ?? ''),
            value: String(d?.code ?? '').trim(),
          }))
          .filter((o) => o.value);
        this.magentoAttributeOptions.set(options);
        this.cdr.markForCheck();
        return options;
      }),
      catchError(() => {
        this.magentoAttributeOptions.set([]);
        this.cdr.markForCheck();
        return of([]);
      }),
    );
  }

  /**
   * Normalize GET magento-mapping response to MagentoFieldMappingVO[].
   * Handles: raw array, HttpResponse.body, wrapped { data/content/pageContent }, or JSON string.
   */
  private normalizeMagentoMappingResponse(
    res: unknown,
  ): MagentoFieldMappingVO[] {
    let parsed: unknown = res;
    if (typeof res === 'string') {
      try {
        parsed = JSON.parse(res) as unknown;
      } catch {
        return [];
      }
    }
    if (Array.isArray(parsed)) return parsed as MagentoFieldMappingVO[];
    if (parsed && typeof parsed === 'object') {
      const o = parsed as Record<string, unknown>;
      // HttpResponse: { body: T }
      const body = o['body'];
      if (Array.isArray(body)) return body as MagentoFieldMappingVO[];
      const arr = o['data'] ?? o['content'] ?? o['pageContent'];
      if (Array.isArray(arr)) return arr as MagentoFieldMappingVO[];
    }
    return [];
  }

  /**
   * Map GET v1/online/shop/{shopId}/magento-mapping response to ProductFieldMappingRow[].
   * Sorts by sortOrder.
   */
  private mapMagentoMappingApiToRows(
    voList: MagentoFieldMappingVO[],
  ): ProductFieldMappingRow[] {
    if (!Array.isArray(voList) || voList.length === 0) return [];
    const sorted = [...voList].sort((a, b) => {
      const soA = (a as any).sortOrder ?? (a as any).sort_order ?? 0;
      const soB = (b as any).sortOrder ?? (b as any).sort_order ?? 0;
      return Number(soA) - Number(soB);
    });
    const dataTypeValues = new Set([
      'STRING',
      'DECIMAL',
      'BOOLEAN',
      'INTEGER',
      'DATETIME',
    ]);
    return sorted.map((vo: any) => {
      const code = vo.internalFieldCode ?? vo.internal_field_code ?? '';
      const attr = vo.magentoAttributeCode ?? vo.magento_attribute_code ?? '';
      const dir = vo.syncDirection ?? vo.sync_direction ?? 'BIDIRECTIONAL';
      const syncDir =
        dir === 'INBOUND' || dir === 'OUTBOUND' || dir === 'BIDIRECTIONAL'
          ? dir
          : 'BIDIRECTIONAL';
      const dt = vo.dataType ?? vo.data_type ?? 'STRING';
      const dataType = dataTypeValues.has(dt) ? dt : 'STRING';
      return {
        skuFieldCode: String(code),
        magentoAttribute: String(attr),
        syncDirection: syncDir as ProductFieldMappingRow['syncDirection'],
        dataType: dataType as ProductFieldMappingRow['dataType'],
      };
    });
  }

  /** Build PUT magento-mapping payload from productFieldMappings. */
  private buildMagentoMappingPayload(): MagentoFieldMappingDTO[] {
    const rows = this.productFieldMappings();
    const dirs = new Set(['INBOUND', 'OUTBOUND', 'BIDIRECTIONAL']);
    const types = new Set([
      'STRING',
      'DECIMAL',
      'BOOLEAN',
      'INTEGER',
      'DATETIME',
    ]);
    return rows.map((m) => ({
      internalFieldCode: m.skuFieldCode,
      magentoAttributeCode: m.magentoAttribute ?? '',
      syncDirection: (dirs.has(m.syncDirection ?? '')
        ? m.syncDirection
        : 'BIDIRECTIONAL') as MagentoFieldMappingDTO['syncDirection'],
      dataType: (types.has(m.dataType ?? '')
        ? m.dataType
        : 'STRING') as MagentoFieldMappingDTO['dataType'],
    }));
  }

  /**
   * Build virtualWarehouses payload from the selected virtual warehouse data.
   * Used when saving any channel that selects a virtual warehouse.
   */
  private buildVirtualWarehousesPayload(warehouseName: string): {
    virtualWarehouses?: Array<{
      virtualWarehouseId?: number;
      virtualWarehouseName?: string;
      typeCode?: string;
      allocationValue?: number;
    }>;
  } {
    if (!warehouseName) return {};
    const selected = this.onlineStoreWarehouseOptionsService
      .getVirtualWarehouseByName()
      .get(warehouseName);
    if (!selected) return {};

    // Read allocation from form; apply defaults based on virtual warehouse type
    const formAllocation = this.formData()?.allocationValue;
    const typeCode = selected.typeCode ?? VIRTUAL_WAREHOUSE_TYPE.DEFAULT;
    let allocationValue: number | undefined =
      typeof formAllocation === 'number' ? formAllocation : undefined;

    if (typeCode === VIRTUAL_WAREHOUSE_TYPE.DEFAULT) {
      allocationValue = allocationValue ?? 100;
    }

    return {
      virtualWarehouses: [
        {
          virtualWarehouseId: selected.id,
          virtualWarehouseName: selected.name,
          typeCode,
          allocationValue,
        },
      ],
    };
  }

  /** Load SKU mapping fields from GET /v1/goods/sku/mapping-fields (goods-service). Replaces DEFAULT_SKU_PRODUCT_FIELDS. */
  loadSkuMappingFieldsFromApi(): Observable<SkuProductFieldOption[]> {
    this.skuProductFieldOptionsLoading.set(true);
    return this.goodsSkuController.getMappingFields().pipe(
      switchMap((response: unknown) => {
        if (response instanceof Blob) {
          return from(
            response
              .text()
              .then((text) => {
                const parsed = JSON.parse(text) as unknown;
                let list: unknown[] = [];
                if (Array.isArray(parsed)) list = parsed;
                else if (parsed && typeof parsed === 'object') {
                  const o = parsed as Record<string, unknown>;
                  const arr = o['data'] ?? o['content'] ?? o['pageContent'];
                  if (Array.isArray(arr)) list = arr;
                }
                return list as Record<string, unknown>[];
              })
              .catch((e: unknown) => {
                throw e instanceof Error ? e : new Error(String(e));
              }),
          );
        }
        let list: unknown[] = [];
        if (Array.isArray(response)) list = response;
        else if (response && typeof response === 'object') {
          const arr =
            (response as Record<string, unknown>)['data'] ??
            (response as Record<string, unknown>)['content'] ??
            (response as Record<string, unknown>)['pageContent'];
          if (Array.isArray(arr)) list = arr;
        }
        return of(list as Record<string, unknown>[]);
      }),
      map((list: Record<string, unknown>[]) => {
        const options: SkuProductFieldOption[] = list
          .map((item) => ({
            code: typeof item['code'] === 'string' ? item['code'].trim() : '',
            label:
              typeof (item['label'] ?? item['labelKey'] ?? item['code']) ===
              'string'
                ? String(
                    item['label'] ?? item['labelKey'] ?? item['code'],
                  ).trim()
                : '',
            required: !!(item['required'] ?? false),
          }))
          .filter((f) => f.code);
        this.skuProductFieldOptions.set(options);
        this.skuProductFieldOptionsLoading.set(false);
        this.cdr.markForCheck();
        return options;
      }),
      catchError((err) => {
        this.skuProductFieldOptionsLoading.set(false);
        this.cdr.markForCheck();
        return of([]);
      }),
    );
  }

  private loadFormDataFromApi(apiItem: any): void {
    // Unwrap when API returns { data: T }, { result: T } or { result: { data: T } }
    let item =
      apiItem && typeof apiItem === 'object' && 'data' in apiItem
        ? apiItem.data
        : apiItem;
    if (item && typeof item === 'object' && item.data) item = item.data;
    if (item && typeof item === 'object' && item.result) item = item.result;
    if (item && typeof item === 'object' && item.data) item = item.data;
    if (item == null || typeof item !== 'object') {
      return;
    }
    // Store original API data for merging during save
    this.originalApiData.set(item);

    // Set channel type for conditional options
    const channelType = item.channelTypeCode || '';
    this.selectedChannelType.set(channelType);

    // Always set form data first so form fields and section order are correct
    this.setFormDataAndSectionsFromApi(item, channelType);

    const isMagento = this.isMagentoChannelFromData(item);

    if (isMagento && (this.editMode || this.viewMode)) {
      this.loadMagentoFieldsOnEditView(item);
    }
  }

  private loadMagentoFieldsOnEditView(item: any): void {
    this.tableSections.set([this.buildProductFieldMappingSection()]);
    this.cdr.markForCheck();
    const shopId = item?.id ?? this.route.snapshot.params['id'];
    const numShopId = shopId == null ? 0 : Number(shopId);
    const loadFields$ = this.loadSkuMappingFieldsFromApi();
    const loadMagentoAttrs$ =
      numShopId > 0
        ? this.loadMagentoAttributeOptions(numShopId)
        : of([] as { label: string; value: string }[]);
    const loadMagentoMapping$ =
      numShopId > 0
        ? this.onlineShopService.getFieldMappings(numShopId).pipe(
            switchMap((res: unknown) => {
              if (res instanceof Blob) {
                return from(BlobConverterUtil.convertToJson(res)).pipe(
                  map((parsed) => this.normalizeMagentoMappingResponse(parsed)),
                );
              }
              return of(this.normalizeMagentoMappingResponse(res));
            }),
            catchError(() => of([] as MagentoFieldMappingVO[])),
          )
        : of([] as MagentoFieldMappingVO[]);
    forkJoin({
      fields: loadFields$,
      magentoAttrs: loadMagentoAttrs$,
      magentoMapping: loadMagentoMapping$,
    }).subscribe({
      next: ({ magentoAttrs, magentoMapping }) => {
        this.magentoAttributeOptions.set(magentoAttrs);
        const mappings = this.mapMagentoMappingApiToRows(magentoMapping);
        this.productFieldMappings.set(mappings);
        this.updateFieldMappingTableSection();
        this.cdr.markForCheck();
        setTimeout(() => {
          this.updateFieldMappingTableSection();
          this.cdr.markForCheck();
        }, 0);
      },
      error: () => {
        this.updateFieldMappingTableSection();
        this.cdr.markForCheck();
      },
    });
  }

  private setFormDataAndSectionsFromApi(
    apiItem: any,
    channelType: string,
  ): void {
    const platformCode =
      apiItem.channelType ||
      apiItem.onlineStoreTypeCode ||
      apiItem.channelSubTypeCode ||
      '';
    // channelPlatform, webhookSecret from API (camelCase or snake_case)
    const channelPlatform =
      apiItem.channelPlatform ?? apiItem.channel_platform ?? null;
    const webhookSecret = apiItem.webhookSecret ?? apiItem.webhook_secret ?? '';
    const websitePlatform =
      apiItem.websitePlatform ||
      apiItem.ecommercePlatform ||
      channelPlatform ||
      null;
    const formDataToSet = {
      name: apiItem.name || '',
      code: apiItem.code || '',
      channelTypeCode: channelType,
      channelSubTypeCode: platformCode,
      websitePlatform,
      channelPlatform,
      magentoAuthType: apiItem.magentoAuthType || null,
      magentoConsumerKey: apiItem.magentoConsumerKey || '',
      magentoConsumerSecret: apiItem.magentoConsumerSecret || '',
      magentoAccessToken: apiItem.magentoAccessToken || '',
      magentoAccessTokenSecret: apiItem.magentoAccessTokenSecret || '',
      magentoUsername: apiItem.magentoUsername || '',
      magentoPassword: apiItem.magentoPassword || '',
      webhookSecret,
      manager: apiItem.manager || '',
      relationPhysicalShopId: apiItem.relationPhysicalShopId || null,
      description: apiItem.description || '',
      url: apiItem.url || '',
      lazadaAppKey: isLazadaOnlineStore(apiItem) ? apiItem.accessKey || '' : '',
      lazadaAppSecret: isLazadaOnlineStore(apiItem) ? apiItem.secretKey || '' : '',
      shopeePartnerId: isShopeeOnlineStore(apiItem) ? apiItem.accessKey || '' : '',
      shopeePartnerKey: isShopeeOnlineStore(apiItem) ? apiItem.secretKey || '' : '',
      channelWarehouseName:
        apiItem.channelWarehouseName ||
        (apiItem.virtualWarehouses?.[0]?.virtualWarehouseName ?? ''),
      allocationValue:
        apiItem.virtualWarehouses?.[0]?.allocationValue ?? undefined,
      deleteFlag: false,
      tenantId: 1,
    };
    this.formData.set(formDataToSet);
  }

  /** Determine if a channel is Magento based on backend fields. */
  private isMagentoChannelFromData(data: any): boolean {
    if (!data || typeof data !== 'object') return false;
    const d = data as Record<string, any>;
    const channelTypeCode = d['channelTypeCode'] ?? d['channel_type_code'];
    const channelType = d['channelType'] ?? d['channel_type'];
    const channelPlatform =
      d['channelPlatform'] ??
      d['channel_platform'] ??
      d['websitePlatform'] ??
      d['ecommercePlatform'];
    // New Magento structure: MARKETPLACE + WEBSITE + MAGENTO
    if (
      channelTypeCode === 'MARKETPLACE' &&
      channelType === 'WEBSITE' &&
      channelPlatform === 'MAGENTO'
    ) {
      return true;
    }
    // Backward compatibility: only websitePlatform/ecommercePlatform
    return channelPlatform === 'MAGENTO';
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'save') {
      // Local validation gate: do not call API when form is invalid
      if (this.formData()?.websitePlatform !== 'MAGENTO') {
        this.clearMagentoFieldValidatorsWhenHidden();
      }
      const form = this.addFormComponent?.form;
      const invalid = form?.invalid ?? true;
      if (form && invalid) {
        form.markAllAsTouched();
        return;
      }
      // Prevent multiple API calls if already saving
      if (this.loading()) {
        return;
      }
      // Set loading state immediately to prevent race conditions
      this.loading.set(true);
      this.saveOnlineStoreData(event.data);
    } else if (event.action === 'cancel') {
      this.router.navigate([this.config.listRoute]);
    }
  }

  canDeactivate(): boolean {
    if (this.viewMode || this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  onFormValueChanges(formValue: any): void {
    // Update form data first
    const previousChannelType = this.formData()?.channelTypeCode;
    const previousPlatform = this.formData()?.channelSubTypeCode;
    this.formData.set(formValue);

    // Platform decides whether the warehouse dropdown lists virtual (kho ảo) vs logical/physical
    // warehouses. The dropdown caches its first load (often before the platform was set), so when
    // the platform changes, reset the selection and force a re-fetch with the right warehouse type.
    if (
      formValue.channelSubTypeCode !== undefined &&
      formValue.channelSubTypeCode !== previousPlatform
    ) {
      const whControl = this.addFormComponent?.form.get('channelWarehouseName');
      if (whControl?.value) {
        whControl.setValue(null, { emitEvent: false });
      }
      this.addFormComponent?.reloadPaginatedField('channelWarehouseName');
    }

    // Update selected channel type when it changes
    if (
      formValue.channelTypeCode !== undefined &&
      formValue.channelTypeCode !== previousChannelType
    ) {
      this.selectedChannelType.set(formValue.channelTypeCode);
      // Clear channel sub-type when channel type changes
      if (formValue.channelTypeCode !== previousChannelType) {
        this.formData.update((data) => ({
          ...data,
          channelSubTypeCode: null,
          websitePlatform: null,
        }));
      }
    }
    // Clear website platform and Magento fields when platform is no longer WEBSITE
    if (
      formValue.channelSubTypeCode !== undefined &&
      formValue.channelSubTypeCode !== 'WEBSITE'
    ) {
      const current = this.formData();
      if (current?.websitePlatform || current?.magentoAuthType) {
        this.formData.update((data) => ({
          ...data,
          websitePlatform: null,
          magentoAuthType: null,
          magentoConsumerKey: '',
          magentoConsumerSecret: '',
          magentoAccessToken: '',
          magentoAccessTokenSecret: '',
          magentoUsername: '',
          magentoPassword: '',
        }));
        this.clearMagentoFieldValidatorsWhenHidden();
      }
    }
    if (
      formValue.channelSubTypeCode !== undefined &&
      formValue.channelSubTypeCode !== 'LAZADA'
    ) {
      const current = this.formData();
      if (current?.lazadaAppKey || current?.lazadaAppSecret) {
        this.formData.update((data) => ({
          ...data,
          lazadaAppKey: '',
          lazadaAppSecret: '',
        }));
      }
    }
    if (
      formValue.channelSubTypeCode !== undefined &&
      formValue.channelSubTypeCode !== 'SHOPEE'
    ) {
      const current = this.formData();
      if (current?.shopeePartnerId || current?.shopeePartnerKey) {
        this.formData.update((data) => ({
          ...data,
          shopeePartnerId: '',
          shopeePartnerKey: '',
        }));
      }
    }
    if (
      formValue.websitePlatform !== undefined &&
      formValue.websitePlatform !== 'MAGENTO'
    ) {
      this.clearMagentoFieldValidatorsWhenHidden();
    }
    this.clearMagentoCredentialsOnAuthChange(formValue.magentoAuthType);

    // Ánh xạ trường sản phẩm only on Edit/View — hide on Add (Thêm)
    const storeId = this.route.snapshot.params['id'];
    if (!storeId) {
      this.tableSections.update((sections) =>
        sections.filter((s) => s.name !== PRODUCT_FIELD_MAPPING_SECTION_NAME),
      );
    }

    // Trigger change detection to update dropdown options
    this.cdr.markForCheck();
  }

  private clearMagentoCredentialsOnAuthChange(
    authType: string | undefined,
  ): void {
    if (authType === undefined) return;
    if (authType !== 'OAUTH_1A') {
      const current = this.formData();
      if (
        current?.magentoConsumerKey ||
        current?.magentoConsumerSecret ||
        current?.magentoAccessToken ||
        current?.magentoAccessTokenSecret
      ) {
        this.formData.update((data) => ({
          ...data,
          magentoConsumerKey: '',
          magentoConsumerSecret: '',
          magentoAccessToken: '',
          magentoAccessTokenSecret: '',
        }));
      }
    }
    if (authType !== 'INTEGRATION_TOKEN') {
      const current = this.formData();
      if (current?.magentoUsername || current?.magentoPassword) {
        this.formData.update((data) => ({
          ...data,
          magentoUsername: '',
          magentoPassword: '',
        }));
      }
    }
  }

  /**
   * when websitePlatform is not Magento, clear validators and values for Magento fields to avoid blocking Save
   * required validator → form invalid. Call to clear validators + value for these controls.
   */
  private clearMagentoFieldValidatorsWhenHidden(): void {
    const form = this.addFormComponent?.form;
    if (!form) return;
    const magentoControlNames = [
      'magentoAuthType',
      'magentoConsumerKey',
      'magentoConsumerSecret',
      'magentoAccessToken',
      'magentoAccessTokenSecret',
      'magentoUsername',
      'magentoPassword',
      'webhookSecret',
    ];
    magentoControlNames.forEach((name) => {
      const ctrl = form.get(name);
      if (ctrl) {
        ctrl.clearValidators();
        ctrl.updateValueAndValidity({ emitEvent: false });
        if (name === 'magentoAuthType')
          ctrl.setValue(null, { emitEvent: false });
        else if (name !== 'webhookSecret')
          ctrl.setValue('', { emitEvent: false });
      }
    });
  }

  /**
   * Handle section action (e.g., create warehouse, test connection).
   * Test connection button is shown only for Magento; handler runs Magento validation.
   */
  onSectionAction(event: {
    section: string;
    action: string;
    data?: any;
  }): void {
    if (event.action === 'create_warehouse') {
      if (this.warehouseMenu && event.data?.event) {
        this.warehouseMenu.toggle(event.data.event);
      }
    }
  }

  private saveOnlineStoreData(formData: any): void {
    try {
      const storeId = this.route.snapshot.params['id'];
      if (this.editMode && storeId) {
        this.saveInEditMode(formData, storeId);
      } else {
        this.saveInAddMode(formData);
      }
    } catch (error) {
      this.loading.set(false);
      console.error('Error saving online store data:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'onlineStore.messages.create_error',
        ),
      });
    }
  }

  private saveInEditMode(formData: any, storeId: string): void {
    const originalData = this.originalApiData();
    const isMagento = formData.websitePlatform === 'MAGENTO';
    const warehouseName =
      formData.channelWarehouseName || originalData?.channelWarehouseName || '';
    const warehouseMap =
      this.onlineStoreWarehouseOptionsService.getWarehouseNameToIdMap();
    const selectedVirtual = this.onlineStoreWarehouseOptionsService
      .getVirtualWarehouseByName()
      .get(warehouseName);
    const isVirtualWarehouse = !!selectedVirtual;
    const physicalWarehouseId = isVirtualWarehouse
      ? undefined
      : (warehouseMap.get(warehouseName) ??
        originalData?.physicalWarehouseId ??
        undefined);
    const onlineStoreTypeCode =
      formData.channelSubTypeCode ||
      originalData?.channelSubTypeCode ||
      originalData?.channelType ||
      originalData?.channelPlatform ||
      '';
    const channelTypeCode = isMagento
      ? 'MARKETPLACE'
      : formData.channelTypeCode || originalData?.channelTypeCode || '';
    const channelType = isMagento ? 'WEBSITE' : onlineStoreTypeCode;
    const onlineShopDTO = {
      id: Number(storeId),
      deleteFlag: false,
      tenantId: 1,
      name: formData.name || originalData?.name || '',
      code: formData.code || originalData?.code || '',
      channelTypeCode,
      channelType,
      ...(isMagento ? {} : { onlineStoreTypeCode }),
      manager: formData.manager || originalData?.manager || '',
      invoiceTypeCode: originalData?.invoiceTypeCode || '',
      relationPhysicalShopId:
        formData.relationPhysicalShopId === undefined
          ? (originalData?.relationPhysicalShopId ?? null)
          : formData.relationPhysicalShopId,
      description: formData.description || originalData?.description || '',
      url: formData.url || originalData?.url || '',
      accessKey: isLazadaChannelCode(onlineStoreTypeCode)
        ? (formData.lazadaAppKey || originalData?.accessKey || '')
        : isShopeeChannelCode(onlineStoreTypeCode)
        ? (formData.shopeePartnerId || originalData?.accessKey || '')
        : '',
      secretKey: isLazadaChannelCode(onlineStoreTypeCode)
        ? (formData.lazadaAppSecret || originalData?.secretKey || '')
        : isShopeeChannelCode(onlineStoreTypeCode)
        ? (formData.shopeePartnerKey || originalData?.secretKey || '')
        : '',
      channelWarehouseName: isVirtualWarehouse ? null : warehouseName,
      ...(isVirtualWarehouse
        ? this.buildVirtualWarehousesPayload(warehouseName)
        : { physicalWarehouseId }),
      ...(formData.websitePlatform === 'MAGENTO' && {
        channelPlatform:
          formData.channelPlatform ??
          originalData?.channelPlatform ??
          'MAGENTO',
        magentoAuthType:
          formData.magentoAuthType || originalData?.magentoAuthType || '',
        ...(formData.magentoAuthType === 'OAUTH_1A' && {
          magentoConsumerKey: formData.magentoConsumerKey ?? '',
          magentoConsumerSecret: formData.magentoConsumerSecret ?? '',
          magentoAccessToken: formData.magentoAccessToken ?? '',
          magentoAccessTokenSecret: formData.magentoAccessTokenSecret ?? '',
        }),
        ...(formData.magentoAuthType === 'INTEGRATION_TOKEN' && {
          magentoUsername: formData.magentoUsername ?? '',
          magentoPassword: formData.magentoPassword ?? '',
        }),
        webhookSecret:
          formData.webhookSecret ?? originalData?.webhookSecret ?? undefined,
        productFieldMappingConfig: serializeFieldMappingToConfig(
          this.productFieldMappings(),
        ),
        orderFieldMappingConfig: serializeFieldMappingToConfig(
          this.orderFieldMappings(),
        ),
      }),
      ...(originalData && {
        status: originalData.status,
        createdBy: originalData.createdBy,
        creationDate: originalData.creationDate,
        lastUpdatedBy: originalData.lastUpdatedBy,
        lastUpdateDate: originalData.lastUpdateDate,
        objectVersionNumber: originalData.objectVersionNumber,
      }),
    };
    this.onlineShopService
      .save1(onlineShopDTO, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        switchMap(() => {
          if (!isMagento) return of(undefined);
          const payload = this.buildMagentoMappingPayload();
          return this.onlineShopService.saveFieldMappings(
            Number(storeId),
            payload,
            'body',
            false,
          );
        }),
      )
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'onlineStore.messages.update_success',
            ),
          });
          if (isShopeeChannelCode(onlineStoreTypeCode)) {
            this.channelIntegrationService
              .getShopeeAuthorizationUrl(Number(storeId), 'response')
              .subscribe({
                next: (authResponse: HttpResponse<any>) => {
                  this.handleShopeeRedirect(authResponse);
                },
                error: () => this.router.navigate([this.config.listRoute]),
              });
          } else {
            setTimeout(() => this.router.navigate([this.config.listRoute]), 1000);
          }
        },
        error: (error: any) => {
          this.loading.set(false);
          console.error('Error updating online store:', error);
          this.messageService.add(
            this.channelService.handleBackendValidationError(error, 'update'),
          );
        },
      });
  }

  private saveInAddMode(formData: any): void {
    const isMagento = formData.websitePlatform === 'MAGENTO';
    const warehouseName = formData.channelWarehouseName || '';
    const warehouseMap =
      this.onlineStoreWarehouseOptionsService.getWarehouseNameToIdMap();
    const selectedVirtual = this.onlineStoreWarehouseOptionsService
      .getVirtualWarehouseByName()
      .get(warehouseName);
    const isVirtualWarehouse = !!selectedVirtual;
    const physicalWarehouseId =
      !isVirtualWarehouse && warehouseName
        ? (warehouseMap.get(warehouseName) ?? undefined)
        : undefined;
    const onlineStoreTypeCode = formData.channelSubTypeCode || '';
    const channelTypeCode = isMagento
      ? 'MARKETPLACE'
      : formData.channelTypeCode || '';
    const channelType = isMagento ? 'WEBSITE' : onlineStoreTypeCode;
    const onlineShopDTO = {
      name: formData.name || '',
      code: formData.code || '',
      channelTypeCode,
      channelType,
      ...(isMagento ? {} : { onlineStoreTypeCode }),
      manager: formData.manager || '',
      invoiceTypeCode: '',
      relationPhysicalShopId: formData.relationPhysicalShopId ?? null,
      description: formData.description || '',
      url: formData.url || '',
      accessKey: isLazadaChannelCode(onlineStoreTypeCode)
        ? (formData.lazadaAppKey || '')
        : isShopeeChannelCode(onlineStoreTypeCode)
        ? (formData.shopeePartnerId || '')
        : '',
      secretKey: isLazadaChannelCode(onlineStoreTypeCode)
        ? (formData.lazadaAppSecret || '')
        : isShopeeChannelCode(onlineStoreTypeCode)
        ? (formData.shopeePartnerKey || '')
        : '',
      channelWarehouseName: isVirtualWarehouse ? null : warehouseName,
      ...(isVirtualWarehouse
        ? this.buildVirtualWarehousesPayload(warehouseName)
        : { physicalWarehouseId }),
      ...(formData.websitePlatform === 'MAGENTO' && {
        channelPlatform: 'MAGENTO',
        magentoAuthType: formData.magentoAuthType || '',
        ...(formData.magentoAuthType === 'OAUTH_1A' && {
          magentoConsumerKey: formData.magentoConsumerKey ?? '',
          magentoConsumerSecret: formData.magentoConsumerSecret ?? '',
          magentoAccessToken: formData.magentoAccessToken ?? '',
          magentoAccessTokenSecret: formData.magentoAccessTokenSecret ?? '',
        }),
        ...(formData.magentoAuthType === 'INTEGRATION_TOKEN' && {
          magentoUsername: formData.magentoUsername ?? '',
          magentoPassword: formData.magentoPassword ?? '',
        }),
        webhookSecret: formData.webhookSecret ?? undefined,
        productFieldMappingConfig: serializeFieldMappingToConfig(
          this.productFieldMappings(),
        ),
        orderFieldMappingConfig: serializeFieldMappingToConfig(
          this.orderFieldMappings(),
        ),
      }),
      status: true,
      deleteFlag: false,
      tenantId: 1,
    };
    this.onlineShopService
      .save1(onlineShopDTO, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'onlineStore.messages.create_success',
            ),
          });

          if (isShopeeChannelCode(onlineStoreTypeCode)) {
            this.onlineShopService.showDetailsByCode(formData.code, 'body', false).subscribe({
              next: (response: any) => {
                const processShops = (shopsList: any[]) => {
                  const shop = Array.isArray(shopsList) ? shopsList.find((s) => s.code === formData.code) : null;
                  if (shop && shop.id) {
                    this.channelIntegrationService.getShopeeAuthorizationUrl(Number(shop.id), 'response').subscribe({
                      next: (authResponse: HttpResponse<any>) => {
                        this.handleShopeeRedirect(authResponse);
                      },
                      error: () => this.router.navigate([this.config.listRoute]),
                    });
                  } else {
                    this.router.navigate([this.config.listRoute]);
                  }
                };

                if (response instanceof Blob) {
                  BlobConverterUtil.convertToJson(response).then((data: any) => {
                    processShops(data);
                  }).catch((err: any) => {
                    console.error('Error parsing Blob to shop object:', err);
                    this.router.navigate([this.config.listRoute]);
                  });
                } else {
                  processShops(response);
                }
              },
              error: (err) => {
                console.error('Error querying showDetailsByCode:', err);
                this.router.navigate([this.config.listRoute]);
              },
            });
          } else {
            setTimeout(() => this.router.navigate([this.config.listRoute]), 1000);
          }
        },
        error: (error: any) => {
          this.loading.set(false);
          console.error('Error creating online store:', error);
          this.messageService.add(
            this.channelService.handleBackendValidationError(error, 'create'),
          );
        },
      });
  }

  private handleShopeeRedirect(authResponse: HttpResponse<any>): void {
    const responseBody = authResponse.body;
    if (responseBody && responseBody instanceof Blob) {
      const reader = new FileReader();
      reader.onload = () => {
        const authUrl = reader.result as string;
        if (authUrl) {
          window.location.href = authUrl;
        } else {
          this.router.navigate([this.config.listRoute]);
        }
      };
      reader.onerror = () => this.router.navigate([this.config.listRoute]);
      reader.readAsText(responseBody);
    } else {
      const authUrl = typeof responseBody === 'string' ? responseBody : '';
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        this.router.navigate([this.config.listRoute]);
      }
    }
  }

  /** True when current channel is Lazada (for showing product list section in view mode). */
  private isLazadaChannel(): boolean {
    return isLazadaOnlineStore({
      channelSubTypeCode: this.formData()?.channelSubTypeCode,
      channelType: this.originalApiData()?.channelType,
    });
  }

  /** True when current channel is Shopee. */
  private isShopeeChannel(): boolean {
    return isShopeeOnlineStore({
      channelSubTypeCode: this.formData()?.channelSubTypeCode,
      channelType: this.originalApiData()?.channelType,
    });
  }

  /** True when channel status is false (inactive); used to disable product list actions. */
  private isChannelInactive(): boolean {
    return this.originalApiData()?.status === false;
  }

  /** Extract error message from API error for product list load; fallback to generic message. */
  private getProductListErrorMessage(error: any): string {
    const body = error?.error;
    const fromBody =
      (typeof body === 'string' && body) ||
      body?.message ||
      body?.detail ||
      body?.msg ||
      body?.error;
    if (fromBody && typeof fromBody === 'string') {
      return fromBody;
    }
    if (error?.message && typeof error.message === 'string') {
      return error.message;
    }
    return this.translateService.instant(
      'onlineStore.messages.load_product_list_error',
    );
  }

  /** Build and set the product list table section for view mode (Lazada only). */
  private createProductListTableSection(onlineStoreId: string): void {
    if (!onlineStoreId) {
      console.error('createProductListTableSection: onlineStoreId is missing');
      return;
    }
    const isLazada = isLazadaOnlineStoreFormContext({
      formData: this.formData(),
      originalApiData: this.originalApiData(),
    });
    const isShopee = this.isShopeeChannel();
    const qtyLabel = isShopee
      ? 'third_party.goods.table.columns.platform_quantity'
      : 'onlineStore.form.product_list.table.columns.lazada_quantity';

    const searchFormConfig: FormFieldConfig[] = [
      {
        key: 'spuCode',
        label: 'onlineStore.form.product_list.search.fields.product_code',
        type: 'text',
        placeholder:
          'onlineStore.form.product_list.search.placeholders.enter_product_code',
      },
      {
        key: 'spuName',
        label: 'onlineStore.form.product_list.search.fields.product_name',
        type: 'text',
        placeholder:
          'onlineStore.form.product_list.search.placeholders.enter_product_name',
      },
      {
        key: 'skuCode',
        label: 'onlineStore.form.product_list.search.fields.sku_code',
        type: 'text',
        placeholder:
          'onlineStore.form.product_list.search.placeholders.enter_sku_code',
      },
      {
        key: 'skuName',
        label: 'onlineStore.form.product_list.search.fields.sku_name',
        type: 'text',
        placeholder:
          'onlineStore.form.product_list.search.placeholders.enter_sku_name',
      },
      {
        key: 'status',
        label: 'onlineStore.form.product_list.search.fields.status',
        type: 'multiselect',
        options: [
          {
            label: 'onlineStore.form.product_list.status.draft',
            value: 'DRAFT',
          },
          {
            label: 'onlineStore.form.product_list.status.active',
            value: 'ACTIVE',
          },
        ],
        placeholder:
          'onlineStore.form.product_list.search.placeholders.select_status',
        showClear: true,
      },
    ];

    const columns: TableColumn[] = [
      {
        label: 'onlineStore.form.product_list.table.columns.category',
        prop: 'category',
        width: 150,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.product_code',
        prop: 'productCode',
        width: 120,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.product_name',
        prop: 'productName',
        width: 200,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.sku_code',
        prop: 'skuCode',
        width: 120,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.sku_name',
        prop: 'skuName',
        width: 150,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.sale_price',
        prop: 'salePrice',
        width: 120,
        align: 'right',
        headerAlign: 'center',
        type: 'custom',
        template: 'currencyTemplate',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.promotion_price',
        prop: 'promotionPrice',
        width: 120,
        align: 'right',
        headerAlign: 'center',
        type: 'custom',
        template: 'currencyTemplate',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.available_stock',
        prop: 'availableStock',
        width: 120,
        align: 'right',
        headerAlign: 'center',
        headerTooltip: 'inventory_lookup.table.columns.available_tooltip',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.current_stock',
        prop: 'stockNum',
        width: 120,
        align: 'right',
        headerAlign: 'center',
        headerTooltip: 'inventory_lookup.table.columns.on_hand_tooltip',
      },
      {
        label: qtyLabel,
        prop: 'lazadaQuantity',
        width: 140,
        align: 'right',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.sales_price',
        prop: 'salesPrice',
        width: 120,
        align: 'right',
        headerAlign: 'center',
        type: 'custom',
        template: 'currencyTemplate',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.status',
        prop: 'status',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        type: 'status',
      },
      // Only include actions column if user has update permission
      ...(this.canUpdate
        ? [
            {
              label: 'onlineStore.form.product_list.table.columns.actions',
              prop: 'actions',
              width: 250,
              align: 'center' as const,
              headerAlign: 'center' as const,
              type: 'actions' as const,
            },
          ]
        : []),
    ];

    // Define flexible columns (can be shown/hidden via column selector)
    const flexibleColumns: TableColumn[] = [
      {
        label: 'onlineStore.form.product_list.table.columns.sku_name',
        prop: 'skuName',
        width: 150,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.category',
        prop: 'category',
        width: 150,
        align: 'left',
        headerAlign: 'center',
      },
    ];

    // Define default columns (always shown)
    const defaultColumns: TableColumn[] = [
      {
        label: 'onlineStore.form.product_list.table.columns.product_code',
        prop: 'productCode',
        width: 120,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.sku_code',
        prop: 'skuCode',
        width: 120,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.product_name',
        prop: 'productName',
        width: 200,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.available_stock',
        prop: 'availableStock',
        width: 120,
        align: 'right',
        headerAlign: 'center',
        headerTooltip: 'inventory_lookup.table.columns.available_tooltip',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.current_stock',
        prop: 'stockNum',
        width: 120,
        align: 'right',
        headerAlign: 'center',
        headerTooltip: 'inventory_lookup.table.columns.on_hand_tooltip',
      },
      {
        label: qtyLabel,
        prop: 'lazadaQuantity',
        width: 140,
        align: 'right',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.sales_price',
        prop: 'salesPrice',
        width: 120,
        align: 'right',
        headerAlign: 'center',
        type: 'custom',
        template: 'currencyTemplate',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.status',
        prop: 'status',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        type: 'status',
      },
      // Only include actions column if user has update permission
      ...(this.canUpdate
        ? [
            {
              label: 'onlineStore.form.product_list.table.columns.actions',
              prop: 'actions',
              width: 250,
              align: 'center' as const,
              headerAlign: 'center' as const,
              type: 'actions' as const,
            },
          ]
        : []),
    ];

    const showProductList = isLazada || isShopee;

    this.productListLoading.set(true);
    const tableSection: TableSection = {
      title: 'onlineStore.form.product_list.title',
      name: 'product_list',
      show: showProductList,
      columns: columns,
      data: this.productListData(),
      loading: true,
      totalRecords: this.productListTotalRecords(),
      rows: this.productListRows(),
      first: this.productListCurrentPage() * this.productListRows(),
      searchFormConfig: searchFormConfig,
      primaryButton: {
        label: 'onlineStore.form.product_list.actions.new',
        severity: 'info' as const,
        icon: 'add',
        show:
          this.canUpdate &&
          this.permissionService.canDoAction('channel.online_store.create'),
        disabled: this.isChannelInactive(),
      },
      disableRowActions: !this.canUpdate || this.isChannelInactive(),
      onSearch: (searchData: any) =>
        this.onProductSearch(searchData, onlineStoreId),
      onReset: () => this.onProductReset(onlineStoreId),
      onprimaryButtonClick: () => {
        this.onProductNew(onlineStoreId);
      },
      onPageChange: (event: any) =>
        this.onProductPageChange(event, onlineStoreId),
      useAdvancedTable: true,
      defaultColumns: defaultColumns,
      flexibleColumns: flexibleColumns,
    };

    const sections: TableSection[] = [tableSection];

    // Append product and order field mapping sections for Magento (shared reusable component)
    const currentData = this.formData();
    const originalData = this.originalApiData();
    const isMagentoChannel =
      this.isMagentoChannelFromData(currentData) ||
      this.isMagentoChannelFromData(originalData);

    if (isMagentoChannel && (this.editMode || this.viewMode)) {
      sections.push(this.buildProductFieldMappingSection());
    }

    this.tableSections.set(sections);
    this.cdr.markForCheck();
    // Product list and /v1/channel-integration/product API are for Lazada or Shopee
    if (isLazada || isShopee) {
      this.loadProducts(onlineStoreId);
    } else {
      this.productListLoading.set(false);
    }
  }

  /** Appends one optional field mapping row (from Add dropdown). */
  addFieldMappingRow(skuFieldCode: string): void {
    this.productFieldMappings.update((list) => [
      ...list,
      {
        skuFieldCode,
        magentoAttribute: '',
        syncDirection: 'BIDIRECTIONAL',
        dataType: 'STRING',
      },
    ]);
    setTimeout(() => {
      this.updateFieldMappingTableSection();
      this.cdr.markForCheck();
    }, 150);
  }

  private getRequiredSkuFieldCodes(): string[] {
    return this.skuProductFieldOptions()
      .filter((f) => f.required)
      .map((f) => f.code);
  }

  /** Build product field mapping table section using shared reusable builder. */
  private buildProductFieldMappingSection(): TableSection {
    return buildProductFieldMappingTableSection({
      mappings: this.productFieldMappings(),
      fieldsConfig: this.skuProductFieldOptions(),
      requiredFieldCodes: this.getRequiredSkuFieldCodes(),
      translate: this.translateService.instant.bind(this.translateService),
      viewMode: this.viewMode,
      insertAfterSection: 'onlineStore.form.sections.channel_warehouse',
      onAddRow: (code) => this.addFieldMappingRow(code),
      onAddDropdownMenuHide: () => {
        this.updateFieldMappingTableSection();
        this.cdr.markForCheck();
      },
      magentoAttributeOptions: this.magentoAttributeOptions(),
    });
  }

  /** Refresh the product field mapping section data and dropdown in tableSections. */
  private updateFieldMappingTableSection(): void {
    const sections = this.tableSections();
    const idx = sections.findIndex(
      (s) => s.name === PRODUCT_FIELD_MAPPING_SECTION_NAME,
    );
    if (idx === -1) return;
    const updated = buildProductFieldMappingTableSection({
      mappings: this.productFieldMappings(),
      fieldsConfig: this.skuProductFieldOptions(),
      requiredFieldCodes: this.getRequiredSkuFieldCodes(),
      translate: this.translateService.instant.bind(this.translateService),
      viewMode: this.viewMode,
      insertAfterSection: 'onlineStore.form.sections.channel_warehouse',
      onAddRow: (code) => this.addFieldMappingRow(code),
      onAddDropdownMenuHide: () => {
        this.updateFieldMappingTableSection();
        this.cdr.markForCheck();
      },
      magentoAttributeOptions: this.magentoAttributeOptions(),
    });
    this.tableSections.set(sections.map((s, i) => (i === idx ? updated : s)));
    this.cdr.markForCheck();
  }

  /** Load product list from channel-integration API and update table section. */
  private loadProducts(onlineStoreId: string): void {
    const isLazada = isLazadaOnlineStoreFormContext({
      formData: this.formData(),
      originalApiData: this.originalApiData(),
    });
    const isShopee = this.isShopeeChannel();
    if (!isLazada && !isShopee) {
      this.productListLoading.set(false);
      return;
    }
    this.productListLoading.set(true);
    this.updateProductListTableSection();

    const searchParams = this.productListSearchParams();

    // Build filter payload
    const filterPayload: any = {};
    if (searchParams.spuCode) {
      filterPayload.spuCode = searchParams.spuCode;
    }
    if (searchParams.spuName) {
      filterPayload.spuName = searchParams.spuName;
    }
    if (searchParams.skuCode) {
      filterPayload.skuCode = searchParams.skuCode;
    }
    if (searchParams.skuName) {
      filterPayload.skuName = searchParams.skuName;
    }
    if (
      searchParams.status &&
      Array.isArray(searchParams.status) &&
      searchParams.status.length > 0
    ) {
      filterPayload.status = searchParams.status;
    }

    // Add pagination if needed
    const currentPage = this.productListCurrentPage();
    const rows = this.productListRows();
    if (currentPage !== undefined && rows !== undefined) {
      filterPayload.page = currentPage;
      filterPayload.size = rows;
    }

    const shopId = Number.parseInt(onlineStoreId, 10);
    if (Number.isNaN(shopId)) {
      console.error('Invalid shopId:', onlineStoreId);
      this.productListLoading.set(false);
      return;
    }

    // Get base path
    const basePath = this.onlineShopService.configuration.basePath || '';
    const url = `${basePath}/v1/channel-integration/product`;

    // Build query parameters
    let queryParams = new HttpParams();
    queryParams = queryParams.set('shopId', shopId.toString());

    // Build headers
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    // Create HTTP request
    const request = new HttpRequest<any>('POST', url, filterPayload || {}, {
      headers: headers,
      params: queryParams,
      reportProgress: false,
      responseType: 'json',
    });

    this.httpClient
      .request<any>(request)
      .pipe(
        switchMap((event: any) => {
          // Handle HttpResponse
          if (event instanceof HttpResponse) {
            let response = event.body;
            // Handle Blob response
            if (response instanceof Blob) {
              return from(BlobConverterUtil.convertToJson(response));
            }
            return of(response || {});
          }
          // If not HttpResponse, try to handle as direct response
          if (event && typeof event === 'object') {
            if (event instanceof Blob) {
              return from(BlobConverterUtil.convertToJson(event));
            }
            return of(event);
          }
          return of({});
        }),
        catchError((error) => {
          console.error('Error loading products:', error);
          const detail = this.getProductListErrorMessage(error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail,
          });
          return of({ pageContent: [], totalElements: 0 });
        }),
        finalize(() => {
          this.productListLoading.set(false);
          this.updateProductListTableSection();
        }),
      )
      .subscribe({
        next: (response: any) => {
          const products =
            response?.pageContent || response?.content || response?.data || [];
          const totalRecords = response?.totalElements || response?.total || 0;

          const mappedProductsList = products.map((product: any) => {
            const stockNum = product.stockNum ?? product.stock_num ?? 0;
            // Available stock is computed by IMS (Stock Service), not re-derived here from raw
            // stockNum/reservedNum.
            const availableStock = product.available ?? product.available_num ?? 0;
            return {
              id: product.id?.toString() || '',
              category: product.categoryName || product.category || '',
              productCode: product.spuCode || product.productCode || '',
              productName: product.spuName || product.productName || '',
              skuCode: product.skuCode || product.sku_code || '',
              skuName: product.skuName || product.sku_name || '',
              salePrice:
                product.salesPrice ??
                product.marketPrice ??
                product.salePrice ??
                0,
              salesPrice:
                product.salesPrice ??
                product.marketPrice ??
                product.salePrice ??
                0,
              promotionPrice:
                product.activityPrice || product.promotionPrice || 0,
              availableStock,
              stockNum,
              lazadaQuantity:
                product.lazadaQuantity ?? product.lazada_quantity ?? 0,
              status: product.status || 'DRAFT',
            };
          });

          const mappedProducts = products.map((product: any) => {
            const stockNum = product.stockNum ?? product.stock_num ?? 0;
            // Available stock is computed by IMS (Stock Service), not re-derived here from raw
            // stockNum/reservedNum.
            const availableStock = product.available ?? product.available_num ?? 0;
            return {
              id: product.id?.toString() || '',
              category: product.categoryName || product.category || '',
              productCode: product.spuCode || product.productCode || '',
              productName: product.spuName || product.productName || '',
              skuCode: product.skuCode || product.sku_code || '',
              skuName: product.skuName || product.sku_name || '',
              salePrice:
                product.salesPrice ??
                product.marketPrice ??
                product.salePrice ??
                0,
              salesPrice:
                product.salesPrice ??
                product.marketPrice ??
                product.salePrice ??
                0,
              promotionPrice:
                product.activityPrice || product.promotionPrice || 0,
              availableStock,
              stockNum,
              lazadaQuantity:
                product.lazadaQuantity ?? product.lazada_quantity ?? 0,
              status: product.status || 'DRAFT',
            };
          });

          this.productListData.set(mappedProducts);
          this.productListTotalRecords.set(totalRecords);
        },
      });
  }

  /** Refresh product list table section with current data and loading state. */
  private updateProductListTableSection(): void {
    const sections = this.tableSections();
    const productSection = sections.find((s) => s.name === 'product_list');
    if (productSection) {
      productSection.data = this.productListData();
      productSection.loading = this.productListLoading();
      productSection.totalRecords = this.productListTotalRecords();
      productSection.first =
        this.productListCurrentPage() * this.productListRows();
      productSection.rows = this.productListRows();
      // Create new array reference to trigger change detection
      this.tableSections.set([...sections]);
      // Trigger change detection to ensure spinner is displayed immediately
      this.cdr.markForCheck();
    } else {
      // If product section doesn't exist yet, ensure it's created with loading state
      // This can happen if loadProducts is called before createProductListTableSection
      console.warn(
        'Product list table section not found, cannot update loading state',
      );
    }
  }

  /** Run product list search and reload table. */
  private onProductSearch(searchData: any, onlineStoreId: string): void {
    this.productListSearchParams.set({ ...searchData, page: 0 });
    this.productListCurrentPage.set(0);
    this.loadProducts(onlineStoreId);
  }

  /** Reset product list filters and reload. */
  private onProductReset(onlineStoreId: string): void {
    this.productListSearchParams.set({});
    this.productListCurrentPage.set(0);
    this.productListRows.set(20);
    this.loadProducts(onlineStoreId);
  }

  /** Handle product list pagination and reload. */
  private onProductPageChange(event: any, onlineStoreId: string): void {
    const page =
      event.page === undefined
        ? Math.floor((event.first || 0) / (event.rows || 20))
        : event.page;
    this.productListCurrentPage.set(page);
    this.productListRows.set(event.rows || 20);
    this.productListSearchParams.set({
      ...this.productListSearchParams(),
      page: page,
      size: event.rows || 20,
    });
    this.loadProducts(onlineStoreId);
  }

  /** Navigate to add new product listing. */
  private onProductNew(onlineStoreId: string): void {
    if (onlineStoreId) {
      this.router.navigate([
        `/channel/online-store/${onlineStoreId}/product-listing/add`,
      ]);
    }
  }

  private handleMappingSectionAction(action: string, data: any): void {
    if (action === 'cellValueChange' && data) {
      const { rowData, prop, value } = data as {
        rowData: any;
        prop: string;
        value: any;
      };
      if (rowData?.skuFieldCode == null) return;
      const updatableProps = ['magentoAttribute', 'syncDirection', 'dataType'];
      if (updatableProps.includes(prop)) {
        this.productFieldMappings.update((list) =>
          list.map((m) =>
            m.skuFieldCode === rowData.skuFieldCode
              ? {
                  ...m,
                  ...(prop === 'magentoAttribute' && {
                    magentoAttribute: value ?? '',
                  }),
                  ...(prop === 'syncDirection' && {
                    syncDirection: value ?? 'BIDIRECTIONAL',
                  }),
                  ...(prop === 'dataType' && { dataType: value ?? 'STRING' }),
                }
              : m,
          ),
        );
      }
    }
    if (action === 'delete' && data?.skuFieldCode != null) {
      const code = data.skuFieldCode as string;
      if (this.getRequiredSkuFieldCodes().includes(code)) return;
      this.productFieldMappings.update((list) =>
        list.filter((m) => m.skuFieldCode !== code),
      );
      this.updateFieldMappingTableSection();
    }
  }

  /** Handle product list row actions: view, edit, delete. */
  onTableAction(event: { section: string; action: string; data?: any }): void {
    const isMappingSection =
      event.section === 'onlineStore.form.product_field_mapping.title' ||
      event.section === PRODUCT_FIELD_MAPPING_SECTION_NAME;
    if (isMappingSection) {
      this.handleMappingSectionAction(event.action, event.data);
      return;
    }

    const onlineStoreId = this.route.snapshot.paramMap.get('id');
    if (!onlineStoreId) {
      console.error('Online store ID is missing');
      return;
    }

    const productId = event.data?.id || event.data?.productId || '';
    if (!productId && (event.action === 'view' || event.action === 'edit')) {
      console.error(
        'Product ID is missing for',
        event.action,
        'data:',
        event.data,
      );
      return;
    }

    switch (event.action) {
      case 'view':
        this.router.navigate([
          `/channel/online-store/${onlineStoreId}/product-listing/view/${productId}`,
        ]);
        break;
      case 'edit':
        this.router.navigate(
          [
            `/channel/online-store/${onlineStoreId}/product-listing/edit/${productId}`,
          ],
          {
            state: {
              availableStock: event.data?.availableStock,
            },
          },
        );
        break;
      case 'delete':
        this.onProductDelete(productId, onlineStoreId);
        break;
    }
  }

  /** Show delete confirmation dialog; calls DELETE /v1/channel-integration/product/{id} (DRAFT or INACTIVE only). */
  private onProductDelete(productId: string, onlineStoreId: string): void {
    this.productDeleteDialogMessage.set(
      'onlineStore.form.product_list.messages.confirm_delete',
    );
    this.productDeleteDialogTitle.set(
      'onlineStore.form.product_list.dialog.delete_title',
    );
    this.productDeleteDialogButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.productDeleteDialogAction.set('deleteProduct');
    this.productDeleteDialogData.set({ productId, onlineStoreId });
    this.showProductDeleteDialog.set(true);
  }

  /** Handle product delete confirmation dialog result. */
  onProductDeleteDialogAction(action: string): void {
    this.showProductDeleteDialog.set(false);
    if (action === 'confirm') {
      const data = this.productDeleteDialogData();
      if (data) {
        this.executeProductDelete(data.productId, data.onlineStoreId);
      }
    }
    this.productDeleteDialogData.set(null);
  }

  /** Get user-facing error message from API error (err.error), or undefined. */
  private getApiErrorMessage(err: any): string | undefined {
    const body = err?.error;
    if (body == null) return undefined;
    if (typeof body === 'object') {
      const msg = body.message ?? body.msg ?? body.error;
      return typeof msg === 'string' && msg.length > 0 ? msg : undefined;
    }
    if (typeof body === 'string' && body.length > 0) {
      try {
        const parsed = JSON.parse(body) as {
          message?: string;
          msg?: string;
          error?: string;
        };
        const msg = parsed?.message ?? parsed?.msg ?? parsed?.error;
        return typeof msg === 'string' && msg.length > 0 ? msg : body;
      } catch {
        return body;
      }
    }
    return undefined;
  }

  /** Call DELETE /v1/channel-integration/product/{id}; show success or error toast and reload list. */
  private executeProductDelete(productId: string, onlineStoreId: string): void {
    const basePath = this.onlineShopService.configuration.basePath || '';
    const url = `${basePath}/v1/channel-integration/product/${productId}`;
    const successDetail = this.translateService.instant(
      'onlineStore.form.product_list.messages.delete_success',
    );
    const errorDetailFallback = this.translateService.instant(
      'onlineStore.form.product_list.messages.delete_error',
    );

    this.httpClient
      .delete(url, { observe: 'response', responseType: 'text' })
      .pipe(
        catchError((err: any) => {
          if (err?.status >= 200 && err?.status < 300) {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: successDetail,
            });
            this.loadProducts(onlineStoreId);
            return of(null);
          }
          const apiMessage = this.getApiErrorMessage(err);
          const detail = apiMessage ?? errorDetailFallback;
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail,
          });
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (
          response !== null &&
          response.status >= 200 &&
          response.status < 300
        ) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: successDetail,
          });
          this.loadProducts(onlineStoreId);
        }
      });
  }
}
